import {
    doppeActionTypeCreateSettingsResolvingFunction,
    doppeActionTypeDefineMetadata
} from '../../client-server-common/doppe-action-types/doppe-action-type';
import {DoppeActionTypeContactFormViewerLang} from './doppe-action-type-contact-form-viewer.lang';
import type {DoppeFormActionSettings} from '../../client-server-common/types/doppe-form-action-settings';
import {
    getDefaultDoppeFormActionSettings,
    prepareFormSettingsForViewer
} from '../../client-server-common/types/doppe-form-action-settings';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import type {DoppeHideableValue} from '../../client-server-common';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingSpecialSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    DoppeAutomationTriggers,
    doppePremiumFeatureIsDisabled,
    DoppePremiumFeatures,
} from '../../client-server-common';
import {hideableValueCreateHidden, immerProduce, RichTextContent} from '@wix/devzai-utils-common';

export namespace DoppeActionTypeContactForm {

    export interface ActionSettings extends DoppeFormActionSettings{
        images: WixMediaResource[] | null;
        title: DoppeHideableValue<string>;
        description: DoppeHideableValue<RichTextContent>;

        subscribeButtonText: string;
        collectEmail: boolean;
        collectPhoneNumber: boolean;
        collectName: boolean;
        collectAddress: boolean;
        collectZip: boolean;
        collectMessage: boolean;
    }
}

export const doppeActionTypeContactFormMetadata = doppeActionTypeDefineMetadata<DoppeActionTypeContactForm.ActionSettings>({
    id: 'co.hopp.action/contact-form',
    resolveActionSettings: doppeActionTypeCreateSettingsResolvingFunction({
        collectEmail: true,
        collectPhoneNumber: true,
        collectName: true,
        collectAddress: true,
        collectZip: true,
        collectMessage: false,
        subscribeButtonText: DoppeActionTypeContactFormViewerLang.defaultTexts.subscribeButtonText,
        images: null,
        title: hideableValueCreateHidden(''),
        description: hideableValueCreateHidden(''),
        ...getDefaultDoppeFormActionSettings()
    }),
    actionSettingsMetadata: {
        contentSettingsMetadata: {
            images: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.MultipleMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
            title: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
            description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
            subscribeButtonText: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityActionButtonText),
            customConsentText: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
            customThankYouMessage: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
            customEmailForNotifications: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
            creatorsListId: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.ListOfTexts, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified),
            linkedWixSiteListId: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.ListOfTexts, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified)
        },
        supportsActionCtaButtons: false,
        supportsContentSections: false
    },
    actionListItemSettingsMetadata: false,
    isWidgetOpener: true,
    isFormAction: true,
    getSupportedAutomationTrigger: () => DoppeAutomationTriggers.ContactFormSubmitted,
    prepareForViewer: actionSettings => {
        prepareFormSettingsForViewer(actionSettings);
    },
    isPremiumAction: (premiumFeaturesStatus) => {
        return doppePremiumFeatureIsDisabled(premiumFeaturesStatus, DoppePremiumFeatures.creators_form_submission)
    },
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    canRenderInPage: true,
    resolveUsedMedia: actionSettings => {
        return actionSettings.images ?? [];
    },
    resolveMainMedia: actionSettings => {
        return actionSettings.images ? actionSettings.images[0] ?? null : null;
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            if (actionSettings.images) {
                actionSettings.images = actionSettings.images.map(
                    image => wixMediaResourceReplaceExternalImage(image, externalImagesUrlToImageResourceMap))
            }
        })
    },
})